import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../../shared/admin-grid.directive';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

export interface DrilldownTab {
  code: string;
  name: string;
  data: any;
  active: boolean;
  loading: boolean;
  cached: boolean;
  gridConfig: AdminGridConfig;
  gridParams: Record<string, any>;
}

@Component({
    selector: 'gtw-form-view-drilldown',
    templateUrl: './drilldown-grid.component.html',
    styleUrls: ['./drilldown-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DrilldownGridComponent implements OnChanges {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  @Input() visible = false;
  @Input() currentTheme = 'theme-black';
  @Input() GENERAL_CONFIG: any = {};
  @Input() showTabBar = true;

  @Output() closed = new EventEmitter<void>();
  @Output() gridFormCallback = new EventEmitter<AdminGridCallbackEvent>();
  /** Parent hides tree/PDF panes (legacy element-view vs dashboard). */
  @Output() workspaceModeChange = new EventEmitter<void>();

  tabs: DrilldownTab[] = [];
  activeTab: DrilldownTab | null = null;
  formReviewActive = true;

  private readonly DOMESTIC_ACTION_ID = 32033;
  private readonly FOREIGN_ACTION_ID = 32440;
  private readonly DOMESTIC_GRID_ID = '5719';
  private readonly FOREIGN_GRID_ID = '5859';

  loading = false;

  @HostBinding('class.drilldown-active')
  get drilldownPanelActive(): boolean {
    return !!this.activeTab && !this.formReviewActive;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.visible && !this.visible) {
      this.formReviewActive = true;
      this.activeTab = null;
    }
  }

  addTab(drilldownData: any): void {
    const code = drilldownData.leName + '-' + drilldownData.elementName;
    const existing = this.tabs.find(t => t.code === code);

    if (existing) {
      this.gtwSnackbarService.openSnackBar('Tab is already open', 'gtw-snackbar--yellow');
      this.activateTab(existing);
      return;
    }

    const isForeign = drilldownData.sourceSystem === 'F';
    const actionId = isForeign ? this.FOREIGN_ACTION_ID : this.DOMESTIC_ACTION_ID;
    const gridId = isForeign ? this.FOREIGN_GRID_ID : this.DOMESTIC_GRID_ID;

    const gridParams: Record<string, any> = {
      sso_id: drilldownData.sso,
      tax_year: drilldownData.taxYear,
      scenario: drilldownData.scenario,
      jcd_key: drilldownData.jcdKey,
      source_system: drilldownData.sourceSystem,
      summary_level: drilldownData.summaryLevel,
      me_codes: drilldownData.meCodes,
      combination_key: drilldownData.combinationKeys,
      form: drilldownData.form,
      schedule: drilldownData.schedule,
      part_no: drilldownData.partNo,
      line: drilldownData.line,
      element: drilldownData.elementName
    };

    const tab: DrilldownTab = {
      code,
      name: drilldownData.leName,
      data: drilldownData,
      active: false,
      loading: false,
      cached: false,
      gridConfig: {
        actionId,
        gridId,
        extraParams: gridParams,
        autoLoad: false,
        cacheKey: `form-view-drilldown-${code}`
      },
      gridParams
    };

    this.tabs.push(tab);
    this.activateTab(tab);
  }

  activateTab(tab: DrilldownTab): void {
    this.formReviewActive = false;
    this.tabs.forEach(t => t.active = false);
    tab.active = true;
    this.activeTab = tab;
    this.workspaceModeChange.emit();
    this.cdr.detectChanges();

    setTimeout(() => {
      if (this.adminGrid) {
        this.adminGrid.loadData(tab.gridParams);
      }
      // gizmoGrid recomputes body/footer height on window resize.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('resize'));
      }
    }, 100);
  }

  /**
   * Legacy PDF Forms tab: `formReviewActive = true` + `ui-sref` dashboard — tabs stay open.
   * Does not emit `closed` (that is only when the last drilldown tab is removed).
   */
  showFormReview(): void {
    this.formReviewActive = true;
    this.tabs.forEach(t => (t.active = false));
    this.activeTab = null;
    this.workspaceModeChange.emit();
    this.cdr.detectChanges();
  }

  /** Last drilldown tab closed — parent may return to dashboard. */
  goToFormReview(): void {
    this.showFormReview();
    this.closed.emit();
  }

  closeTab(tab: DrilldownTab, event: Event): void {
    event.stopPropagation();
    const index = this.tabs.indexOf(tab);
    if (index > -1) {
      this.tabs.splice(index, 1);
    }

    if (tab.active) {
      if (this.tabs.length === 0) {
        this.goToFormReview();
      } else {
        this.activateTab(this.tabs[Math.max(0, index - 1)]);
      }
    } else {
      this.workspaceModeChange.emit();
    }
    this.cdr.detectChanges();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    if (event.type === 'form' && event.data?.rowData) {
      this.gridFormCallback.emit(event);
    }
  }

  onGridDataLoaded(data: any): void {
    if (this.activeTab) {
      this.activeTab.loading = false;
      this.activeTab.cached = true;
      this.cdr.detectChanges();
    }
  }

  onLoadingStateChange(isLoading: boolean): void {
    this.loading = isLoading;
    if (this.activeTab) {
      this.activeTab.loading = isLoading;
    }
    this.cdr.detectChanges();
  }

  get activeGridConfig(): AdminGridConfig | null {
    return this.activeTab?.gridConfig || null;
  }

  get displayFilterData(): string {
    if (!this.activeTab) return '';
    return Object.entries(this.activeTab.gridParams)
      .map(([k, v]) => `&${k}=${v}`)
      .join('');
  }

  /**
   * Legacy `form-view-tab.html` shows empty popup fields as blank; PDF often sends `null`
   * as the string "null" — display as `NA` like stage UI.
   */
  formatHeaderField(value: unknown): string {
    if (value === null || value === undefined) {
      return 'NA';
    }
    const raw = String(value).trim();
    if (!raw || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'undefined') {
      return 'NA';
    }
    return raw;
  }

  hasLineTotal(): boolean {
    const raw = this.activeTab?.data?.lineTotal;
    if (raw === null || raw === undefined) {
      return false;
    }
    const s = String(raw).trim();
    return !!(s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined');
  }

  /** Legacy formViewTabCtrl uses Number formatter on line_total (commas stripped first). */
  formatLineTotal(val: string): string {
    if (!this.hasLineTotal()) {
      return '';
    }
    const normalized = String(val).split(',').join('');
    const num = parseFloat(normalized);
    if (isNaN(num)) {
      return String(val).trim();
    }
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  formatNumber(val: string): string {
    return this.formatLineTotal(val);
  }
}
